import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card";
import { Bot, Send, User, MessageSquare, ClipboardList, Mic, Trophy, TrendingUp } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { CareerQuestionnaireMode } from "./CareerQuestionnaireMode";
import { VoiceAssistantMode } from "./VoiceAssistantMode";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface QuestionnaireResults {
  cybersecurity: number;
  frontend: number;
  backend: number;
  programming: number;
}

interface CareerAdvisorDialogProps {
  children: React.ReactNode;
}

type AdvisorMode = "selection" | "chat" | "questionnaire" | "voice" | "results";

export const CareerAdvisorDialog = ({ children }: CareerAdvisorDialogProps) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: "Բարև՜ ձեզ! Ես Global IT Academy-ի AI խորհրդատուն եմ: Պատրաստ եմ օգնել ձեզ ընտրել ձեր հնարավորություններին և հետաքրքրություններին համապատասխան դասընթացը: Կարո՞ղ եք պատմել ինձ ձեր նախկին փորձի և նպատակների մասին IT ոլորտում:"
    }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<AdvisorMode>("selection");
  const [questionnaireResults, setQuestionnaireResults] = useState<QuestionnaireResults | null>(null);
  const { toast } = useToast();

  const handleQuestionnaireResults = (results: QuestionnaireResults) => {
    setQuestionnaireResults(results);
    setMode("results");
    
    // Generate recommendation message based on results
    const recommendations = generateRecommendations(results);
    const recommendationMessage: Message = {
      role: "assistant",
      content: recommendations
    };
    
    setMessages(prev => [...prev, recommendationMessage]);
  };

  const generateRecommendations = (results: QuestionnaireResults): string => {
    const sortedResults = Object.entries(results)
      .sort(([,a], [,b]) => b - a)
      .map(([field, score]) => ({ field, score }));

    const topRecommendation = sortedResults[0];
    const secondRecommendation = sortedResults[1];

    const courseMap = {
      cybersecurity: "Կիբեռանվտանգություն",
      frontend: "Frontend Ծրագրավորում", 
      backend: "Backend Ծրագրավորում",
      programming: "Ծրագրավորման Լեզուներ"
    };

    const descriptionMap = {
      cybersecurity: "Կիբեռանվտանգության մասնագետները պաշտպանում են տեղեկատվական համակարգերը։ Ուսուցանվում են Network Security, Ethical Hacking, Penetration Testing։",
      frontend: "Frontend ծրագրավորողները ստեղծում են կայքերի վիզուալ մասը։ Ուսուցանվում են HTML, CSS, JavaScript, React։",
      backend: "Backend ծրագրավորողները պատասխանատու են սերվերային տրամաբանության համար։ Ուսուցանվում են Node.js, Python, Database Design։", 
      programming: "Ծրագրավորման լեզուները հիմքն են ցանկացած ծրագրային մշակման համար։ Ուսուցանվում են Python, JavaScript, Java, C++։"
    };

    return `📊 **Ձեր հարցաշարի արդյունքները:**

🥇 **Առաջարկվող հիմնական ուղղություն:** ${courseMap[topRecommendation.field as keyof typeof courseMap]} (${topRecommendation.score} միավոր)

${descriptionMap[topRecommendation.field as keyof typeof descriptionMap]}

🥈 **Երկրորդ տարբերակ:** ${courseMap[secondRecommendation.field as keyof typeof courseMap]} (${secondRecommendation.score} միավոր)

${descriptionMap[secondRecommendation.field as keyof typeof descriptionMap]}

📚 **Կարող եք ընտրել:**
• 6 ամսյա բազային դասընթաց
• 9 ամսյա նախազորակոչային դասընթաց  
• 18 ամսյա ակադեմիական դասընթաց

🎯 Ցանկանո՞ւմ եք ավելի մանրամասն տեղեկություններ ստանալ որևէ դասընթացի մասին:`;
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { role: "user", content: input.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const response = await fetch(`https://ivluchyoxbfcuwzdqdaj.functions.supabase.co/functions/v1/career-advisor`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Iml2bHVjaHlveGJmY3V3emRxZGFqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDk2MzEwOTMsImV4cCI6MjA2NTIwNzA5M30.eC1VwKtXWTY_Kt5sXvfBbougnhOyXt36nJFJcQTieoA`,
        },
        body: JSON.stringify({ message: userMessage.content }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Խնդիր չի լուծվում");
      }

      if (!response.body) throw new Error("Պատասխան չի ստացվել");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let assistantContent = "";

      const upsertAssistant = (content: string) => {
        setMessages(prev => {
          const lastMessage = prev[prev.length - 1];
          if (lastMessage?.role === "assistant") {
            return prev.map((msg, index) => 
              index === prev.length - 1 ? { ...msg, content } : msg
            );
          } else {
            return [...prev, { role: "assistant", content }];
          }
        });
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantContent += content;
              upsertAssistant(assistantContent);
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Սխալ",
        description: error instanceof Error ? error.message : "Անհայտ սխալ է տեղի ունեցել",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetToSelection = () => {
    setMode("selection");
    setQuestionnaireResults(null);
  };

  const renderSelectionMode = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold mb-2">Ընտրեք հաղորդակցման եղանակը</h3>
        <p className="text-muted-foreground">
          Ֆունկցիոն հարցվելիս մեր AI խորհրդատուն կօգնի ձեզ գտնել իդեալական դասընթացը
        </p>
      </div>

      <div className="grid gap-4">
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setMode("questionnaire")}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <ClipboardList className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold">8 Հարցի հարցաշար</h4>
                <p className="text-sm text-muted-foreground">
                  Կարճ հարցաշար` ճիշտ դասընթացը ընտրելու համար
                </p>
              </div>
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setMode("voice")}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                <Mic className="h-6 w-6 text-secondary-foreground" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold">Ձայնային խորհրդատու</h4>
                <p className="text-sm text-muted-foreground">
                  Խոսեք մեր AI-ի հետ ձայնով և ստացեք պատասխան
                </p>
              </div>
              <Mic className="h-5 w-5 text-secondary-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setMode("chat")}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-accent-foreground" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold">Ազատ զրույց</h4>
                <p className="text-sm text-muted-foreground">
                  Գրավոր չատ մեր AI խորհրդատուի հետ
                </p>
              </div>
              <Bot className="h-5 w-5 text-accent-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderChatMode = () => (
    <div className="flex flex-col h-full">
      <ScrollArea className="flex-1 pr-4">
        <div className="space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex gap-3 ${
                message.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`flex items-start gap-2 max-w-[80%] ${
                  message.role === "user" ? "flex-row-reverse" : "flex-row"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  message.role === "user" 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-secondary text-secondary-foreground"
                }`}>
                  {message.role === "user" ? (
                    <User className="h-4 w-4" />
                  ) : (
                    <Bot className="h-4 w-4" />
                  )}
                </div>
                <div
                  className={`p-3 rounded-lg ${
                    message.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex gap-3 justify-start">
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-secondary text-secondary-foreground">
                  <Bot className="h-4 w-4" />
                </div>
                <div className="bg-muted p-3 rounded-lg">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      <div className="flex gap-2 mt-4">
        <Button variant="ghost" onClick={resetToSelection}>
          Հետ
        </Button>
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Գրեք ձեր հարցը..."
          disabled={isLoading}
          className="flex-1"
        />
        <Button onClick={sendMessage} disabled={isLoading || !input.trim()}>
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  const renderResultsMode = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Trophy className="h-16 w-16 text-primary mx-auto mb-4" />
        <h3 className="text-xl font-semibold mb-2">Ձեր անհատական առաջարկությունները</h3>
      </div>

      <ScrollArea className="max-h-96">
        <div className="space-y-4">
          {messages.slice(-1).map((message, index) => (
            <div key={index} className="bg-muted p-4 rounded-lg">
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="flex gap-2">
        <Button variant="outline" onClick={resetToSelection} className="flex-1">
          Նոր գնահատում
        </Button>
        <Button onClick={() => setMode("chat")} className="flex-1">
          Ավելի մանրամասն
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            AI Մասնագիտական Խորհրդատու
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="flex-1 overflow-y-auto pr-4">
          {mode === "selection" && renderSelectionMode()}
          {mode === "chat" && renderChatMode()}
          {mode === "questionnaire" && (
            <CareerQuestionnaireMode 
              onResults={handleQuestionnaireResults}
              onBack={resetToSelection}
            />
          )}
          {mode === "voice" && (
            <VoiceAssistantMode onBack={resetToSelection} />
          )}
          {mode === "results" && renderResultsMode()}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};